import React from 'react';
import { User, X, Check } from 'lucide-react';
import { Profile } from './useProfiles';
import { toast } from 'sonner';
import { saveSelectedProfileId } from '@/utils/geolocation';
import { useTranslation } from 'react-i18next';

interface ProfileListProps {
  profiles: Profile[];
  session: any;
  handleDeleteProfile: (id: string) => void;
  selectedProfileId?: string;
  onProfileClick?: (id: string) => void;
}

const ProfileList: React.FC<ProfileListProps> = ({ 
  profiles, 
  session, 
  handleDeleteProfile,
  selectedProfileId,
  onProfileClick
}) => {
  const { t } = useTranslation();
  
  const handleProfileSelection = (id: string) => {
    if (onProfileClick) {
      // Validar UUID
      const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
      
      if (!isValidUUID) {
        console.error('ID de perfil inválido:', id);
        toast.error(t('profile.invalidProfileId'));
        return;
      }
      
      // Guardar la selección en localStorage para persistencia
      saveSelectedProfileId(id);
      
      // Llamar al callback proporcionado
      onProfileClick(id);
      
      // Mostrar un toast de confirmación
      const selectedProfile = profiles.find(p => p.id === id);
      if (selectedProfile) {
        toast(t('profile.profileSelected', { name: selectedProfile.name }));
      }
    }
  };
  
  return (
    <div className="space-y-4 mb-6">
      {profiles.map(profile => (
        <div 
          key={profile.id} 
          className={`bg-white p-4 rounded-xl border ${selectedProfileId === profile.id ? 'border-sangre-300 shadow-md' : 'border-gray-100 shadow-sm'} 
            flex justify-between items-center ${onProfileClick ? 'cursor-pointer' : ''} transition-all duration-200`}
          onClick={() => handleProfileSelection(profile.id)}
        >
          <div className="flex items-center">
            <div className={`p-2 rounded-full ${selectedProfileId === profile.id ? 'bg-sangre-50' : 'bg-gray-100'}`}>
              <User className={`w-5 h-5 ${selectedProfileId === profile.id ? 'text-sangre-500' : 'text-gray-500'}`} />
            </div>
            <div className="ml-3">
              <h4 className="font-medium text-sm text-gray-800">{profile.name}</h4>
              <p className="text-xs text-gray-500">{profile.age} {t('profile.years')}</p>
            </div>
          </div>
          
          <div className="flex items-center">
            {selectedProfileId === profile.id && (
              <div className="mr-3 bg-sangre-50 p-1 rounded-full">
                <Check className="w-4 h-4 text-sangre-500" />
              </div>
            )}
            
            {/* Only allow deletion if the profile is not the main user profile */}
            {(profile.id !== '1' && (!session || profile.id !== session.user?.id)) && (
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteProfile(profile.id);
                }}
                className="text-gray-400 hover:text-sangre-500"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      ))}
      
      {!session && profiles.length > 0 && (
        <div className="text-center text-sm text-gray-500 mt-2">
          <p>{t('profile.loginToManage')}</p>
        </div>
      )}
      
      {profiles.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <p>{t('profile.noProfiles')}</p>
        </div>
      )}
    </div>
  );
};

export default ProfileList;
